import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Robot, EnvelopeSimple, Lock, Buildings, ArrowRight, Warning } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Ingresa un email válido';
    }
    if (!password) {
      newErrors.password = 'La contraseña es requerida';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success('¡Bienvenido!');
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
      setErrors({ general: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img src="/images/novagente-logo.png" alt="Novagente" className="h-8" />
          </Link>

          <h1 className="font-black text-3xl tracking-tighter text-[#09090B] mb-2">
            Bienvenido de nuevo
          </h1>
          <p className="text-[#52525B] mb-8">
            Ingresa a tu cuenta para continuar
          </p>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 mb-6 text-sm flex items-center gap-2">
              <Warning size={16} /> {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: null });
                  }}
                  data-testid="login-email"
                  className={`w-full pl-10 pr-4 py-3 border bg-white text-[#09090B] focus:outline-none transition-colors ${
                    errors.email ? 'border-red-500 focus:border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF] focus:border-2'
                  }`}
                  placeholder="tu@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <Warning size={14} /> {errors.email}
                </p>
              )}
            </div>

            <div>
              <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: null });
                  }}
                  data-testid="login-password"
                  className={`w-full pl-10 pr-4 py-3 border bg-white text-[#09090B] focus:outline-none transition-colors ${
                    errors.password ? 'border-red-500 focus:border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF] focus:border-2'
                  }`}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <Warning size={14} /> {errors.password}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? 'Ingresando...' : 'Iniciar sesión'}
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>

          <p className="text-center text-[#52525B] mt-8">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-[#0047FF] font-medium hover:underline">
              Regístrate gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block flex-1 bg-[#F4F4F5] border-l border-[#E4E4E7]">
        <div className="h-full flex items-center justify-center p-16">
          <img 
            src="/images/auth-side.png"
            alt="Office"
            className="w-full max-w-lg object-cover"
          />
        </div>
      </div>
    </div>
  );
}

export function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {};
    if (!companyName.trim()) {
      newErrors.companyName = 'El nombre de empresa es requerido';
    }
    if (!email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Ingresa un email válido';
    }
    if (!password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (password.length < 6) {
      newErrors.password = 'Mínimo 6 caracteres';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      await register(email, password, companyName);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al registrarse';
      toast.error(message);
      setErrors({ general: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <img src="/images/novagente-logo.png" alt="Novagente" className="h-8" />
          </Link>

          <h1 className="font-black text-3xl tracking-tighter text-[#09090B] mb-2">
            Crea tu cuenta
          </h1>
          <p className="text-[#52525B] mb-8">
            Comienza a automatizar tu atención al cliente
          </p>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 mb-6 text-sm flex items-center gap-2">
              <Warning size={16} /> {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                Nombre de tu empresa <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Buildings size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    if (errors.companyName) setErrors({ ...errors, companyName: null });
                  }}
                  data-testid="register-company"
                  className={`w-full pl-10 pr-4 py-3 border bg-white text-[#09090B] focus:outline-none transition-colors ${
                    errors.companyName ? 'border-red-500 focus:border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF] focus:border-2'
                  }`}
                  placeholder="Mi Empresa"
                />
              </div>
              {errors.companyName && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <Warning size={14} /> {errors.companyName}
                </p>
              )}
            </div>

            <div>
              <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: null });
                  }}
                  data-testid="register-email"
                  className={`w-full pl-10 pr-4 py-3 border bg-white text-[#09090B] focus:outline-none transition-colors ${
                    errors.email ? 'border-red-500 focus:border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF] focus:border-2'
                  }`}
                  placeholder="tu@email.com"
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <Warning size={14} /> {errors.email}
                </p>
              )}
            </div>

            <div>
              <label className="block font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: null });
                  }}
                  data-testid="register-password"
                  className={`w-full pl-10 pr-4 py-3 border bg-white text-[#09090B] focus:outline-none transition-colors ${
                    errors.password ? 'border-red-500 focus:border-red-500' : 'border-[#E4E4E7] focus:border-[#0047FF] focus:border-2'
                  }`}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <Warning size={14} /> {errors.password}
                </p>
              )}
              <p className="text-xs text-[#A1A1AA] mt-1">Mínimo 6 caracteres</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-[#0047FF] text-white rounded-xl py-3 font-medium hover:bg-[#0035CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta gratis'}
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>

          <p className="text-center text-[#52525B] mt-8">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-[#0047FF] font-medium hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block flex-1 bg-[#F4F4F5] border-l border-[#E4E4E7]">
        <div className="h-full flex items-center justify-center p-16">
          <img 
            src="/images/logo.png"
            alt="AI Chat"
            className="w-full max-w-lg object-cover"
          />
        </div>
      </div>
    </div>
  );
}
